import React, { useEffect, useRef, useState } from 'react';
import { Box, BoxProps, useMediaQuery } from '@mui/material';

/** True once the element has scrolled into view (immediately where IntersectionObserver is missing). */
export function useInView<T extends Element>(threshold = 0.15) {
  const ref = useRef<T | null>(null);
  const [inView, setInView] = useState(false);

  useEffect(() => {
    const el = ref.current;
    if (!el || typeof IntersectionObserver === 'undefined') {
      setInView(true);
      return;
    }
    const observer = new IntersectionObserver(
      (entries) => {
        if (entries.some((e) => e.isIntersecting)) {
          setInView(true);
          observer.disconnect();
        }
      },
      { threshold }
    );
    observer.observe(el);
    return () => observer.disconnect();
  }, [threshold]);

  return [ref, inView] as const;
}

export function usePrefersReducedMotion() {
  return useMediaQuery('(prefers-reduced-motion: reduce)', { noSsr: true });
}

/** Fades and lifts its children in the first time they scroll into view. */
const Reveal: React.FC<BoxProps & { delay?: number }> = ({ delay = 0, sx, children, ...rest }) => {
  const [ref, inView] = useInView<HTMLDivElement>();
  const reduced = usePrefersReducedMotion();

  return (
    <Box
      ref={ref}
      sx={[
        {
          opacity: inView || reduced ? 1 : 0,
          transform: inView || reduced ? 'none' : 'translateY(28px)',
          transition: reduced
            ? 'none'
            : `opacity .7s ease ${delay}ms, transform .7s cubic-bezier(.2,.7,.2,1) ${delay}ms`,
        },
        ...(Array.isArray(sx) ? sx : [sx]),
      ]}
      {...rest}
    >
      {children}
    </Box>
  );
};

export default Reveal;
